import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import type { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
} from 'ag-grid-community';
import type { AdminUser } from '../../../services/admin.service';
import {
  AdminService,
  type AdminUserSubscriptionUpdate,
} from '../../../services/admin.service';
import { SaveButtonCellRendererComponent } from './save-button-cell-renderer.component';

ModuleRegistry.registerModules([AllCommunityModule]);

const SUBSCRIPTION_TIERS = ['free', '1-month', '3-month'] as const;

@Component({
  selector: 'app-user-management',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './user-management.component.html',
  styleUrl: './user-management.component.scss',
  imports: [AgGridAngular, SaveButtonCellRendererComponent],
})
export class UserManagementComponent implements OnInit {
  protected readonly admin = inject(AdminService);

  @ViewChild(AgGridAngular) gridRef!: AgGridAngular;
  private gridApi: GridApi<AdminUser> | null = null;

  protected readonly users = signal<AdminUser[]>([]);
  protected readonly loading = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly dirtyRows = signal<Set<string>>(new Set());
  protected readonly savingUserId = signal<string | null>(null);

  protected readonly theme = themeQuartz.withParams({
    backgroundColor: '#ffffff',
    foregroundColor: '#0f172a',
    headerBackgroundColor: '#f1f5f9',
    headerTextColor: '#334155',
    accentColor: '#1e40af',
    borderColor: '#e2e8f0',
    oddRowBackgroundColor: 'rgba(0, 0, 0, 0.02)',
    spacing: 10,
    fontSize: 12,
    headerFontSize: 13,
    headerFontWeight: '600',
  });
  protected readonly columnDefs: ColDef<AdminUser>[] = [
    { field: 'userId', headerName: 'User ID', minWidth: 260 },
    { field: 'email', headerName: 'Email', minWidth: 200 },
    { field: 'role', headerName: 'Role', minWidth: 90 },
    { field: 'displayName', headerName: 'Display Name', minWidth: 140 },
    { field: 'profileImageUrl', headerName: 'Profile Image', minWidth: 120 },
    { field: 'preferredLanguage', headerName: 'Language', minWidth: 100 },
    {
      field: 'subscriptionTier',
      headerName: 'Subscription Tier',
      minWidth: 140,
      editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: [...SUBSCRIPTION_TIERS] },
    },
    {
      field: 'subscriptionStartDate',
      headerName: 'Start Date',
      minWidth: 130,
      editable: true,
      cellEditor: 'agDateStringCellEditor',
    },
    {
      field: 'subscriptionEndDate',
      headerName: 'End Date',
      minWidth: 130,
      editable: true,
      cellEditor: 'agDateStringCellEditor',
    },
    { field: 'examDate', headerName: 'Exam Date', minWidth: 120 },
    { field: 'goal', headerName: 'Goal', minWidth: 80 },
    { field: 'createdAt', headerName: 'Created', minWidth: 180 },
    { field: 'updatedAt', headerName: 'Updated', minWidth: 180 },
    {
      colId: 'actions',
      headerName: 'Actions',
      minWidth: 100,
      sortable: false,
      filter: false,
      cellRenderer: SaveButtonCellRendererComponent,
      cellRendererParams: {},
    },
  ];

  protected readonly defaultColDef: ColDef<AdminUser> = {
    flex: 1,
    minWidth: 80,
    filter: true,
    sortable: true,
    resizable: true,
  };

  protected getGridContext(): {
    isDirty: (id: string) => boolean;
    onSave: (id: string) => void;
    getSavingUserId: () => string | null;
  } {
    return {
      isDirty: (userId: string) => this.dirtyRows().has(userId),
      onSave: (userId: string) => this.saveRow(userId),
      getSavingUserId: () => this.savingUserId(),
    };
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  protected loadUsers(): void {
    this.loading.set(true);
    this.error.set(null);
    this.admin
      .fetchAllUsers()
      .then((res) => {
        this.users.set(res.users);
        this.gridApi?.setGridOption('rowData', res.users);
        this.loading.set(false);
      })
      .catch((err) => {
        this.error.set(
          err?.error?.detail ?? err?.message ?? 'Failed to load users'
        );
        this.loading.set(false);
      });
  }

  protected onCellValueChanged(): void {
    // Mark the row as dirty when any editable cell changes
    // We get the event from the template
  }

  protected onGridReady(params: GridReadyEvent<AdminUser>): void {
    this.gridApi = params.api;
    const data = this.users();
    if (data.length > 0) {
      params.api.setGridOption('rowData', data);
    }
  }

  protected onCellValueChangedHandler(event: { data: AdminUser }): void {
    if (event?.data?.userId) {
      this.dirtyRows.update((set) => new Set(set).add(event.data.userId));
      this.gridApi?.refreshCells({ columns: ['actions'], force: true });
    }
  }

  protected saveRow(userId: string): void {
    const row = this.users().find((u) => u.userId === userId);
    if (!row) return;

    const payload: AdminUserSubscriptionUpdate = {
      subscriptionTier: row.subscriptionTier,
      subscriptionStartDate: row.subscriptionStartDate ?? null,
      subscriptionEndDate: row.subscriptionEndDate ?? null,
    };

    this.savingUserId.set(userId);
    this.gridApi?.refreshCells({ columns: ['actions'], force: true });

    this.admin
      .updateUserSubscription(userId, payload)
      .then((updated) => {
        this.users.update((list) =>
          list.map((u) => (u.userId === userId ? { ...u, ...updated } : u))
        );
        this.dirtyRows.update((set) => {
          const next = new Set(set);
          next.delete(userId);
          return next;
        });
        this.gridApi?.refreshCells({ force: true });
      })
      .catch(() => {
        // Error could be shown via a toast; for now we just reset saving state
      })
      .finally(() => {
        this.savingUserId.set(null);
        this.gridApi?.refreshCells({ columns: ['actions'], force: true });
      });
  }
}
